/**
 * O que um arquivo GGUF é, deduzido do nome.
 *
 * Mora sozinho porque duas telas precisam da MESMA resposta: a busca, ao
 * oferecer o download, e a lista do aparelho, ao oferecer o carregamento. Ter
 * duas cópias da regra foi o defeito real — a busca aprendeu a reconhecer
 * peças auxiliares e a lista do aparelho não, então o arquivo recusado lá
 * continuava sendo oferecido aqui, com botão de carregar e tudo.
 *
 * Nem todo GGUF é um modelo. Repositórios de modelos grandes trazem peças no
 * mesmo formato e na mesma pasta, e elas são JUSTAMENTE as menores — portanto
 * as que mais parecem caber num aparelho apertado.
 *
 * Dois casos reais que passaram por aqui:
 *
 *   mtp-gemma-4-26B-A4B-it.gguf ................ 230 MB, num repo de 15,64 GB
 *   Qwen3-VL-8B-Instruct-abliterated.mmproj-Q8_0.gguf ......... 717 MB
 *
 * O segundo escapou da primeira versão desta regra porque o separador antes de
 * `mmproj` era um PONTO, e a regra procurava hífen ou sublinhado. Daí a busca
 * ser por conteúdo do nome, sem exigir separador: falso positivo aqui custa
 * esconder um modelo da lista; falso negativo custa um download inteiro e um
 * erro que ninguém entende.
 */

export type PapelDoArquivo = "modelo" | "projetor" | "rascunho" | "parte";

export interface Classificacao {
  papel: PapelDoArquivo;
  /** Por que não serve sozinho. Vazio quando serve. */
  aviso: string;
}

export function papelPeloNome(nome: string): Classificacao {
  const n = nome.toLowerCase();

  if (n.includes("mmproj")) {
    return {
      papel: "projetor",
      aviso:
        "é o projetor de imagem de um modelo multimodal, não o modelo. Por dentro ele é um " +
        "'clip', que o motor de linguagem não abre.",
    };
  }
  // `mtp` como palavra, para não casar com um modelo que por acaso tenha essas
  // três letras no meio de outra coisa.
  if (/(^|[-_.])(mtp|draft|drafter)([-_.]|$)/.test(n)) {
    return {
      papel: "rascunho",
      aviso:
        "é a peça de predição de múltiplos tokens que acompanha o modelo grande, não um modelo. " +
        "Baixa rápido por ser pequena, e não carrega sozinha.",
    };
  }
  const parte = n.match(/-(\d{5})-of-(\d{5})\./);
  if (parte) {
    return {
      papel: "parte",
      aviso:
        `é a parte ${Number(parte[1])} de ${Number(parte[2])} de um modelo dividido. ` +
        "Sozinha não abre — seriam necessárias todas.",
    };
  }
  return { papel: "modelo", aviso: "" };
}

declare global {
  interface Window {
    __wuweiGgufNomes: { papelPeloNome: typeof papelPeloNome };
  }
}

if (typeof window !== "undefined") {
  window.__wuweiGgufNomes = { papelPeloNome };
}
